import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Grid,
    Paper,
    Divider,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Collapse
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import ListIcon from '@mui/icons-material/List';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { injectIntl } from 'react-intl';
import * as WebhookService from '../../services/webhook.service';
import JsonEditor from '../json-editor';

const PAYLOAD_FORMATS = [
    { value: 'STANDARD', label: 'Padrão Mexx', icon: <CodeIcon /> },
    { value: 'CUSTOM_JSON', label: 'JSON Personalizado', icon: <CodeIcon /> },
    { value: 'CUSTOM_FORM', label: 'Form Data', icon: <ListIcon /> }
];

const GROUP_ICONS = {
    data: '🗄️',
    actor: '👤',
    changes: '🔄',
    lookup: '🔍',
    meta: 'ℹ️',
    external: '🔗' // S3.4 — refs externas capturadas de outros webhooks
};

function WebhookPayloadConfig(props) {
    const {
        isOpen,
        handleClose,
        webhookId,
        subscription,
        eventTypeId,
        eventName,
        eventCode,
        payloadExample,
        onSave,
        intl
    } = props;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Payload config state
    const [payloadFormat, setPayloadFormat] = useState('STANDARD');
    const [customPayloadTemplate, setCustomPayloadTemplate] = useState('');
    const [formDataItems, setFormDataItems] = useState([{ key: '', value: '' }]);

    // Estado para guardar os valores originais do banco (para restaurar ao trocar formato e voltar)
    const [savedFormat, setSavedFormat] = useState('STANDARD');
    const [savedJsonTemplate, setSavedJsonTemplate] = useState('');
    const [savedFormDataItems, setSavedFormDataItems] = useState([{ key: '', value: '' }]);

    // Variables state
    const [variables, setVariables] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    // Form data focused field tracking
    const [focusedFieldIndex, setFocusedFieldIndex] = useState(null);

    // Preview state
    const [previewResult, setPreviewResult] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    // Ref direto para o textarea do JsonEditor (para inserção de variáveis)
    const jsonEditorTextareaRef = useRef(null);

    // Load initial values when opening
    useEffect(() => {
        if (isOpen && subscription) {
            const format = subscription.payloadformat || subscription.payloadFormat || 'STANDARD';
            setPayloadFormat(format);
            setSavedFormat(format);

            const template = subscription.custompayloadtemplate || subscription.customPayloadTemplate;

            if (format === 'CUSTOM_FORM' && template) {
                try {
                    const items = JSON.parse(template);
                    const parsedItems = items.length > 0 ? items : [{ key: '', value: '' }];
                    setFormDataItems(parsedItems);
                    setSavedFormDataItems(parsedItems);
                } catch {
                    setFormDataItems([{ key: '', value: '' }]);
                    setSavedFormDataItems([{ key: '', value: '' }]);
                }
                setCustomPayloadTemplate('');
                setSavedJsonTemplate('');
            } else if (format === 'CUSTOM_JSON' && template) {
                setCustomPayloadTemplate(template);
                setSavedJsonTemplate(template);
                setFormDataItems([{ key: '', value: '' }]);
                setSavedFormDataItems([{ key: '', value: '' }]);
            } else {
                setCustomPayloadTemplate('');
                setSavedJsonTemplate('');
                setFormDataItems([{ key: '', value: '' }]);
                setSavedFormDataItems([{ key: '', value: '' }]);
            }

            setError(null);
            setFocusedFieldIndex(null);
            setPreviewResult(null);
        }
    }, [isOpen, subscription]);

    // Load variables when opening
    useEffect(() => {
        if (isOpen && eventTypeId) {
            loadVariables();
        }
    }, [isOpen, eventTypeId]);

    const loadVariables = async () => {
        setLoading(true);
        try {
            // Busca variaveis do evento + response mappings do webhook em paralelo — o
            // segundo so faz sentido se ja tivermos webhookId (webhook salvo). Novos
            // webhooks nao mostram o grupo external ate o primeiro save (esperado —
            // a config de captura vive na nova Accordion "Capturar dados do retorno").
            const [eventVarsResult, mappingsResult] = await Promise.all([
                WebhookService.getEventVariables(eventTypeId),
                webhookId ? WebhookService.getWebhookResponseMappings(webhookId) : Promise.resolve({ success: false, data: [] })
            ]);

            if (!eventVarsResult.success || !eventVarsResult.data) {
                setError('Erro ao carregar variáveis');
                setLoading(false);
                return;
            }

            const merged = [...eventVarsResult.data];

            // Adiciona grupo "external" no final da lista se houver mappings configurados.
            // Mesmo shape dos outros grupos (groupid + variables[]) — o resto da UI ja
            // renderiza generico.
            if (mappingsResult?.success && Array.isArray(mappingsResult.data) && mappingsResult.data.length > 0) {
                const externalGroup = {
                    groupid: 'external',
                    groupname: intl.formatMessage({ id: 'webhook.variables.group.external' }),
                    variables: mappingsResult.data
                        .filter(m => m.active !== false)
                        .map(m => ({
                            variablepath: `external.${m.externalkeyname}`,
                            variablename: m.externalkeyname,
                            variabledescription: m.description
                                || intl.formatMessage(
                                    { id: 'webhook.variables.external.desc' },
                                    { type: m.externalentitytype, path: m.jsonpath }
                                ),
                            datatype: 'string'
                        }))
                };
                if (externalGroup.variables.length > 0) merged.push(externalGroup);
            }

            setVariables(merged);
            // Expand first group only by default
            const expanded = {};
            if (merged.length > 0) {
                const firstGroup = merged[0];
                const groupKey = firstGroup.groupid || firstGroup.groupId;
                expanded[groupKey] = true;
            }
            setExpandedGroups(expanded);
        } catch (err) {
            setError('Erro ao carregar variáveis');
        } finally {
            setLoading(false);
        }
    };

    const handleFormatChange = (newFormat) => {
        setPayloadFormat(newFormat);
        setPreviewResult(null);

        // Restaurar os valores salvos do banco quando voltar para o formato original
        if (newFormat === savedFormat) {
            // Voltou para o formato original salvo no banco
            if (savedFormat === 'CUSTOM_JSON') {
                setCustomPayloadTemplate(savedJsonTemplate);
            } else if (savedFormat === 'CUSTOM_FORM') {
                setFormDataItems(savedFormDataItems.length > 0 ? savedFormDataItems : [{ key: '', value: '' }]);
            }
        } else if (newFormat === 'STANDARD') {
            // Mudou para STANDARD (mas não era o formato salvo)
            // Não limpa os templates para poder restaurar depois
        } else if (newFormat === 'CUSTOM_JSON') {
            // Mudou para JSON - restaurar template JSON salvo se era o formato original
            if (savedFormat === 'CUSTOM_JSON') {
                setCustomPayloadTemplate(savedJsonTemplate);
            }
            // Se não tinha JSON salvo, mantém vazio ou o que já tinha
        } else if (newFormat === 'CUSTOM_FORM') {
            // Mudou para FORM - restaurar form data salvo se era o formato original
            if (savedFormat === 'CUSTOM_FORM') {
                setFormDataItems(savedFormDataItems.length > 0 ? savedFormDataItems : [{ key: '', value: '' }]);
            } else {
                // Se não tinha form salvo, inicializa vazio
                setFormDataItems([{ key: '', value: '' }]);
            }
        }
    };

    const handleTemplateChange = (event) => {
        setCustomPayloadTemplate(event.target.value);
        setPreviewResult(null);
    };

    const handleFormDataItemChange = (index, field, value) => {
        const newItems = [...formDataItems];
        newItems[index][field] = value;
        setFormDataItems(newItems);
        setPreviewResult(null);
    };

    const handleAddFormDataItem = () => {
        setFormDataItems([...formDataItems, { key: '', value: '' }]);
    };

    const handleRemoveOrClearFormDataItem = (index) => {
        if (formDataItems.length === 1) {
            // If only one item, clear both fields instead of removing
            setFormDataItems([{ key: '', value: '' }]);
        } else {
            // If more than one item, remove the item
            const newItems = formDataItems.filter((_, i) => i !== index);
            setFormDataItems(newItems);
        }
        setPreviewResult(null);
    };

    const insertVariable = useCallback((variablePath) => {
        const variableText = `{{${variablePath}}}`;

        if (payloadFormat === 'CUSTOM_JSON') {
            // Usar o ref direto do textarea do JsonEditor
            const textarea = jsonEditorTextareaRef.current;
            if (textarea) {
                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const newValue = customPayloadTemplate.slice(0, start) + variableText + customPayloadTemplate.slice(end);
                setCustomPayloadTemplate(newValue);

                // Set cursor after inserted variable
                setTimeout(() => {
                    textarea.focus();
                    textarea.setSelectionRange(start + variableText.length, start + variableText.length);
                }, 0);
            } else {
                // Fallback: adicionar no final
                setCustomPayloadTemplate(prev => prev + variableText);
            }
        } else if (payloadFormat === 'CUSTOM_FORM') {
            // For form data, replace the focused field value with the variable
            const targetIndex = focusedFieldIndex !== null ? focusedFieldIndex : formDataItems.length - 1;
            handleFormDataItemChange(targetIndex, 'value', variableText);
        }

        setPreviewResult(null);
    }, [payloadFormat, customPayloadTemplate, formDataItems, focusedFieldIndex]);

    const toggleGroup = (groupId, event) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        setExpandedGroups(prev => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const getTemplateForPreview = () => {
        if (payloadFormat === 'CUSTOM_JSON') {
            return customPayloadTemplate;
        } else if (payloadFormat === 'CUSTOM_FORM') {
            return JSON.stringify(formDataItems.filter(item => item.key.trim() !== ''));
        }
        return null;
    };

    const handlePreview = async () => {
        setPreviewLoading(true);

        try {
            const template = getTemplateForPreview();
            const result = await WebhookService.previewPayload(eventTypeId, payloadFormat, template);

            if (result.success) {
                setPreviewResult(result.data);
            } else {
                setPreviewResult({
                    isvalid: false,
                    isValid: false,
                    errormessage: result.errors?.[0] || 'Erro ao gerar preview',
                    errorMessage: result.errors?.[0] || 'Erro ao gerar preview'
                });
            }
        } catch (err) {
            setPreviewResult({
                isvalid: false,
                isValid: false,
                errormessage: 'Erro ao gerar preview',
                errorMessage: 'Erro ao gerar preview'
            });
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);

        try {
            let template = null;
            if (payloadFormat !== 'STANDARD') {
                template = getTemplateForPreview();
                if (!template || template.trim() === '' || template === '[]') {
                    setError('Template de payload é obrigatório para formatos personalizados');
                    setSaving(false);
                    return;
                }
            }

            const result = await WebhookService.updateSubscriptionPayload(
                webhookId,
                subscription.id,
                payloadFormat,
                template
            );

            if (result.success) {
                if (onSave) {
                    onSave(result.data);
                }
                handleClose();
            } else {
                setError(result.errors?.[0] || 'Erro ao salvar configuração');
            }
        } catch (err) {
            setError('Erro ao salvar configuração');
        } finally {
            setSaving(false);
        }
    };

    // Helper to get property value with fallback for lowercase
    const getVarProp = (variable, propName) => {
        return variable[propName.toLowerCase()] ?? variable[propName];
    };

    const getGroupProp = (group, propName) => {
        return group[propName.toLowerCase()] ?? group[propName];
    };

    const filteredVariables = searchTerm
        ? variables.map(group => ({
            ...group,
            variables: (getGroupProp(group, 'variables') || []).filter(v => {
                const path = getVarProp(v, 'variablePath') || '';
                const name = getVarProp(v, 'variableName') || '';
                return path.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    name.toLowerCase().includes(searchTerm.toLowerCase());
            })
        })).filter(group => (getGroupProp(group, 'variables') || []).length > 0)
        : variables;

    // Format JSON for display
    const formatJsonForDisplay = (jsonString) => {
        if (!jsonString) return '';
        try {
            const parsed = JSON.parse(jsonString);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return jsonString;
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minHeight: '80vh' }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6">
                            Configurar Payload Personalizado
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {eventName} ({eventCode})
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 0 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container sx={{ height: '100%' }}>
                        {/* Left Panel - Variable Picker */}
                        <Grid item xs={12} md={3} sx={{
                            borderRight: '1px solid #e0e0e0',
                            backgroundColor: '#fafafa',
                            maxHeight: '60vh',
                            overflow: 'auto'
                        }}>
                            <Box sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                    Variáveis Disponíveis
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Buscar variável..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{ mb: 2 }}
                                />

                                {filteredVariables.map(group => {
                                    const groupId = getGroupProp(group, 'groupId');
                                    const groupName = getGroupProp(group, 'groupName');
                                    const groupVariables = getGroupProp(group, 'variables') || [];

                                    return (
                                        <Box key={groupId} sx={{ mb: 1 }}>
                                            <Box
                                                onClick={(e) => toggleGroup(groupId, e)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    p: 1,
                                                    cursor: 'pointer',
                                                    backgroundColor: '#f0f0f0',
                                                    borderRadius: 1,
                                                    '&:hover': { backgroundColor: '#e8e8e8' }
                                                }}
                                            >
                                                <Typography sx={{ mr: 1 }}>{GROUP_ICONS[groupId] || '📋'}</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
                                                    {groupName}
                                                </Typography>
                                                {expandedGroups[groupId] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                            </Box>

                                            <Collapse in={expandedGroups[groupId]} timeout="auto" unmountOnExit>
                                                <Box sx={{ pl: 1, pt: 0.5 }}>
                                                    {groupVariables.map(variable => {
                                                        const varId = getVarProp(variable, 'id');
                                                        const varPath = getVarProp(variable, 'variablePath');
                                                        const varName = getVarProp(variable, 'variableName');
                                                        const varDesc = getVarProp(variable, 'variableDescription');
                                                        const varDataType = getVarProp(variable, 'dataType');
                                                        const varExample = getVarProp(variable, 'exampleValue');
                                                        const varIsLookup = getVarProp(variable, 'isLookup');

                                                        return (
                                                            <Tooltip
                                                                key={varId}
                                                                title={
                                                                    <Box>
                                                                        <Typography variant="caption" display="block" sx={{ color: '#fff' }}>
                                                                            {varDesc || varName}
                                                                        </Typography>
                                                                        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: '#fff' }}>
                                                                            Tipo: {varDataType}
                                                                        </Typography>
                                                                        {varExample && (
                                                                            <Typography variant="caption" display="block" sx={{ color: '#fff' }}>
                                                                                Ex: {varExample}
                                                                            </Typography>
                                                                        )}
                                                                        {varIsLookup && (
                                                                            <Chip label="Lookup" size="small" sx={{ mt: 0.5, height: 16, fontSize: 10, color: '#fff' }} />
                                                                        )}
                                                                    </Box>
                                                                }
                                                                placement="right"
                                                                arrow
                                                            >
                                                                <Box
                                                                    onClick={() => payloadFormat !== 'STANDARD' && insertVariable(varPath)}
                                                                    sx={{
                                                                        p: 0.75,
                                                                        mb: 0.5,
                                                                        borderRadius: 1,
                                                                        cursor: payloadFormat !== 'STANDARD' ? 'pointer' : 'default',
                                                                        opacity: payloadFormat === 'STANDARD' ? 0.5 : 1,
                                                                        '&:hover': {
                                                                            backgroundColor: payloadFormat !== 'STANDARD' ? '#e3f2fd' : 'transparent'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Typography variant="caption" display="block" sx={{ fontWeight: 500, color: '#333' }}>
                                                                        {varName}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            fontFamily: 'monospace',
                                                                            color: '#333',
                                                                            fontSize: 10
                                                                        }}
                                                                    >
                                                                        {`{{${varPath}}}`}
                                                                    </Typography>
                                                                </Box>
                                                            </Tooltip>
                                                        );
                                                    })}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Grid>

                        {/* Center Panel - Editor */}
                        <Grid item xs={12} md={5} sx={{ p: 2 }}>
                            <Box sx={{ mb: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="payload-format-label">Formato do Payload</InputLabel>
                                    <Select
                                        labelId="payload-format-label"
                                        id="payload-format-select"
                                        value={payloadFormat}
                                        onChange={(e) => handleFormatChange(e.target.value)}
                                        label="Formato do Payload"
                                    >
                                        {PAYLOAD_FORMATS.map(format => (
                                            <MenuItem key={format.value} value={format.value}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {format.icon}
                                                    {format.label}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            {payloadFormat === 'STANDARD' && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    O payload padrão do Mexx será enviado. Para personalizar, selecione JSON ou Form Data.
                                </Alert>
                            )}

                            {payloadFormat === 'CUSTOM_JSON' && (
                                <Box>
                                    <JsonEditor
                                        value={customPayloadTemplate}
                                        onChange={(newValue) => {
                                            setCustomPayloadTemplate(newValue);
                                            setPreviewResult(null);
                                        }}
                                        placeholder={'{\n  "ticket_id": "{{data.called_id}}",\n  "title": "{{data.title}}",\n  "status": "{{lookup.status.name}}"\n}'}
                                        minHeight={300}
                                        maxHeight={500}
                                        label="JSON Personalizado"
                                        textareaRef={jsonEditorTextareaRef}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        Clique em uma variável à esquerda para inserir no cursor. Arraste o canto inferior direito para redimensionar.
                                    </Typography>
                                </Box>
                            )}

                            {payloadFormat === 'CUSTOM_FORM' && (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                        Campos do Form Data
                                    </Typography>

                                    {formDataItems.map((item, index) => (
                                        <Box key={index} display="flex" gap={1} mb={1}>
                                            <TextField
                                                size="small"
                                                placeholder="Campo"
                                                value={item.key}
                                                onChange={(e) => handleFormDataItemChange(index, 'key', e.target.value)}
                                                sx={{ flex: 1 }}
                                            />
                                            <TextField
                                                size="small"
                                                placeholder="Valor ou {{variavel}}"
                                                value={item.value}
                                                onChange={(e) => handleFormDataItemChange(index, 'value', e.target.value)}
                                                onFocus={() => setFocusedFieldIndex(index)}
                                                sx={{
                                                    flex: 2,
                                                    '& .MuiInputBase-input': {
                                                        fontFamily: 'monospace'
                                                    }
                                                }}
                                            />
                                            <Tooltip title={formDataItems.length === 1 ? "Limpar campos" : "Remover linha"}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveOrClearFormDataItem(index)}
                                                >
                                                    {formDataItems.length === 1 ? <ClearIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    ))}

                                    <Button
                                        startIcon={<AddIcon />}
                                        onClick={handleAddFormDataItem}
                                        size="small"
                                        sx={{ mt: 1 }}
                                    >
                                        Adicionar campo
                                    </Button>

                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Clique no campo de valor e depois em uma variável para inserir.
                                    </Typography>
                                </Box>
                            )}

                            {error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    {error}
                                </Alert>
                            )}
                        </Grid>

                        {/* Right Panel - Preview */}
                        <Grid item xs={12} md={4} sx={{
                            borderLeft: '1px solid #e0e0e0',
                            backgroundColor: '#f5f5f5'
                        }}>
                            <Box sx={{ p: 2 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                        {payloadFormat === 'STANDARD' ? 'Exemplo de Payload' : 'Preview'}
                                    </Typography>
                                    {payloadFormat !== 'STANDARD' && (
                                        <Button
                                            startIcon={previewLoading ? <CircularProgress size={16} /> : <VisibilityIcon />}
                                            onClick={handlePreview}
                                            disabled={previewLoading}
                                            size="small"
                                            variant="outlined"
                                        >
                                            Gerar Preview
                                        </Button>
                                    )}
                                </Box>

                                {payloadFormat === 'STANDARD' && payloadExample && (
                                    <Box>
                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            <CheckCircleIcon color="success" fontSize="small" />
                                            <Typography variant="body2" color="success.main">
                                                Payload padrão do evento
                                            </Typography>
                                        </Box>

                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                            Content-Type: application/json
                                        </Typography>

                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                backgroundColor: '#fff',
                                                maxHeight: 400,
                                                overflow: 'auto',
                                                border: '1px solid #ddd'
                                            }}
                                        >
                                            <pre style={{
                                                margin: 0,
                                                color: '#333',
                                                fontSize: 11,
                                                fontFamily: 'Consolas, Monaco, monospace',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}>
                                                {formatJsonForDisplay(payloadExample)}
                                            </pre>
                                        </Paper>
                                    </Box>
                                )}

                                {payloadFormat === 'STANDARD' && !payloadExample && (
                                    <Alert severity="info">
                                        O payload padrão do Mexx será enviado com os dados do evento.
                                    </Alert>
                                )}

                                {payloadFormat !== 'STANDARD' && !previewResult && (
                                    <Alert severity="info">
                                        Clique em "Gerar Preview" para visualizar o payload com valores de exemplo.
                                    </Alert>
                                )}

                                {payloadFormat !== 'STANDARD' && previewResult && (
                                    <Box>
                                        {/* Status */}
                                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                                            {(previewResult.isvalid || previewResult.isValid) ? (
                                                <>
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                    <Typography variant="body2" color="success.main">
                                                        Template válido
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <ErrorIcon color="error" fontSize="small" />
                                                    <Typography variant="body2" color="error.main">
                                                        {previewResult.errormessage || previewResult.errorMessage}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>

                                        {/* Invalid variables warning */}
                                        {(previewResult.invalidvariables || previewResult.invalidVariables)?.length > 0 && (
                                            <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
                                                <Typography variant="caption">
                                                    Variáveis inválidas: {(previewResult.invalidvariables || previewResult.invalidVariables).join(', ')}
                                                </Typography>
                                            </Alert>
                                        )}

                                        {/* Found variables */}
                                        {(previewResult.variablesfound || previewResult.variablesFound)?.length > 0 && (
                                            <Box mb={2}>
                                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                                    Variáveis encontradas:
                                                </Typography>
                                                <Box display="flex" flexWrap="wrap" gap={0.5}>
                                                    {(previewResult.variablesfound || previewResult.variablesFound).map((v, i) => (
                                                        <Chip
                                                            key={i}
                                                            label={v}
                                                            size="small"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: 10,
                                                                fontFamily: 'monospace'
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Request Headers */}
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                                            Request Headers
                                        </Typography>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                backgroundColor: '#f8f9fa',
                                                mb: 2,
                                                border: '1px solid #ddd'
                                            }}
                                        >
                                            <pre style={{
                                                margin: 0,
                                                color: '#333',
                                                fontSize: 11,
                                                fontFamily: 'Consolas, Monaco, monospace',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}>
                                                {(previewResult.requestheaders || previewResult.requestHeaders)
                                                    ? (typeof (previewResult.requestheaders || previewResult.requestHeaders) === 'object'
                                                        ? Object.entries(previewResult.requestheaders || previewResult.requestHeaders)
                                                            .map(([key, value]) => `${key}: ${value}`)
                                                            .join('\n')
                                                        : (previewResult.requestheaders || previewResult.requestHeaders))
                                                    : `Content-Type: ${previewResult.contenttype || previewResult.contentType || 'application/json'}
Accept: */*
User-Agent: Mexx-Webhook/1.0`
                                                }
                                            </pre>
                                        </Paper>

                                        {/* Request Body (Processed payload) */}
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                            Request Body (Payload Processado)
                                        </Typography>
                                        <Paper
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                backgroundColor: '#fff',
                                                maxHeight: 250,
                                                overflow: 'auto',
                                                border: '1px solid #ddd'
                                            }}
                                        >
                                            <pre style={{
                                                margin: 0,
                                                color: '#333',
                                                fontSize: 11,
                                                fontFamily: 'Consolas, Monaco, monospace',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word'
                                            }}>
                                                {previewResult.processedpayload || previewResult.processedPayload}
                                            </pre>
                                        </Paper>

                                        {/* Response Headers (if available from API) */}
                                        {(previewResult.responseheaders || previewResult.responseHeaders) && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                                                    Response Headers
                                                </Typography>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1.5,
                                                        backgroundColor: '#e8f5e9',
                                                        mb: 2,
                                                        border: '1px solid #c8e6c9'
                                                    }}
                                                >
                                                    <pre style={{
                                                        margin: 0,
                                                        color: '#333',
                                                        fontSize: 11,
                                                        fontFamily: 'Consolas, Monaco, monospace',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        {typeof (previewResult.responseheaders || previewResult.responseHeaders) === 'object'
                                                            ? Object.entries(previewResult.responseheaders || previewResult.responseHeaders)
                                                                .map(([key, value]) => `${key}: ${value}`)
                                                                .join('\n')
                                                            : (previewResult.responseheaders || previewResult.responseHeaders)
                                                        }
                                                    </pre>
                                                </Paper>
                                            </>
                                        )}

                                        {/* Response Body (if available from API) */}
                                        {(previewResult.responsebody || previewResult.responseBody) && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 1 }}>
                                                    Response Body
                                                </Typography>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1.5,
                                                        backgroundColor: '#e8f5e9',
                                                        maxHeight: 200,
                                                        overflow: 'auto',
                                                        border: '1px solid #c8e6c9'
                                                    }}
                                                >
                                                    <pre style={{
                                                        margin: 0,
                                                        color: '#333',
                                                        fontSize: 11,
                                                        fontFamily: 'Consolas, Monaco, monospace',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        {previewResult.responsebody || previewResult.responseBody}
                                                    </pre>
                                                </Paper>
                                            </>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 2 }}>
                <Button onClick={handleClose} variant="outlined">
                    Cancelar
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                    disabled={saving}
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default injectIntl(WebhookPayloadConfig);
